/**
 * SocialMediaManager Component
 *
 * Main view with tabs for All / Drafts / Pending / Scheduled / Published posts.
 */
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  Search,
  Loader2,
  FileText,
  Clock,
  CheckCircle2,
  Calendar,
  Send,
  RefreshCw,
  Settings,
  Facebook,
  Share2,
} from 'lucide-react';

import { PageTitle } from '../shared';

import SocialPostCard from './SocialPostCard';
import { SocialPostListSkeleton } from './SocialPostSkeleton';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { social } from '@/services/api/admin';
import {
  getRememberedPageValue,
  rememberPageValue,
} from '@/components/admin/navigation/adminPlaceMemory';
import { getAdminUser } from '@/components/admin/navigation/sectionAccess';

const TABS = [
  { id: 'all', label: 'All Posts', icon: FileText },
  { id: 'draft', label: 'Drafts', icon: FileText },
  { id: 'pending_review', label: 'Pending Review', icon: Clock },
  { id: 'approved', label: 'Approved', icon: CheckCircle2 },
  { id: 'scheduled', label: 'Scheduled', icon: Calendar },
  { id: 'published', label: 'Published', icon: Send },
];
const TAB_IDS = TABS.map((tab) => tab.id);

const SocialMediaManager = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();

  // State
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [search, setSearch] = useState('');
  const [deletePost, setDeletePost] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Get tab from URL or default to 'all'
  const requestedTab = searchParams.get('tab');
  const activeTab = TAB_IDS.includes(requestedTab)
    ? requestedTab
    : 'all';

  const fetchPosts = async () => {
    setIsLoading(true);
    try {
      const params = {
        page,
        page_size: 20,
      };

      if (activeTab !== 'all') {
        params.status = activeTab;
      }

      if (search) {
        params.search = search;
      }

      const data = await social.listPosts(params);
      setPosts(data.posts);
      setTotal(data.total);
      setTotalPages(data.total_pages || 1);
      setHasNext(data.has_next || false);
      setHasPrev(data.has_prev || false);
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to load posts',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [activeTab, page, search]);

  const handleTabChange = (tab) => {
    rememberPageValue('socialmedia', tab, TAB_IDS, getAdminUser());
    setSearchParams({ tab });
    setPage(1);
  };

  const handleSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handleDuplicate = async (post) => {
    try {
      const newPost = await social.duplicatePost(post.id);
      toast({ title: 'Post duplicated' });
      navigate(`/admin/outreach/social/${newPost.id}`);
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to duplicate post',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (!deletePost) return;

    setIsDeleting(true);
    try {
      await social.deletePost(deletePost.id);
      toast({ title: 'Post deleted' });
      setDeletePost(null);
      fetchPosts();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete post',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const pendingCount = posts.filter((p) => p.status === 'pending_review').length;

  return (
    <div className="space-y-6">
      <PageTitle
        title="Social Media"
        description="Create and schedule posts for Facebook, Instagram, and TikTok"
        icon={<Share2 className="h-6 w-6" />}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="sm:w-auto sm:px-3"
              onClick={() => navigate('/admin/outreach/social/schedule')}
            >
              <Calendar className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Schedule</span>
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="sm:w-auto sm:px-3"
              onClick={() => navigate('/admin/outreach/social/platforms')}
            >
              <Settings className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Connections</span>
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="sm:w-auto sm:px-3"
              onClick={() => navigate('/admin/outreach/social/facebook')}
            >
              <Facebook className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">FB History</span>
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label="FB Manager"
              className="sm:w-auto sm:px-3"
              onClick={() => {
                const tab = getRememberedPageValue(
                  'social.facebook-manager.tab', ['info', 'comments', 'messages'], 'info', getAdminUser(),
                );
                navigate(`/admin/outreach/social/facebook-manager?tab=${encodeURIComponent(tab)}`);
              }}
            >
              <Facebook className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">FB Manager</span>
            </Button>
            <Button
              size="icon"
              className="sm:w-auto sm:px-3"
              onClick={() => navigate('/admin/outreach/social/new')}
            >
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Create Post</span>
            </Button>
          </div>
        }
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <TabsList className="h-auto p-1 inline-flex">
            {TABS.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="gap-0 sm:gap-2 px-2 sm:px-3 data-[state=active]:shadow-sm"
              >
                <tab.icon className="h-4 w-4 shrink-0" />
                <span className="hidden sm:inline">{tab.label}</span>
                {tab.id === 'pending_review' && pendingCount > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                    {pendingCount}
                  </Badge>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Search & Filters */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-none sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500 dark:text-gray-400" />
              <Input
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search posts..."
                className="pl-9"
              />
            </div>
            <Button variant="outline" size="icon" onClick={fetchPosts}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="mt-6">
          {isLoading ? (
            <SocialPostListSkeleton count={5} />
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {search ? 'No posts found' : 'No posts yet'}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mt-1">
                {search
                  ? 'Try adjusting your search'
                  : 'Create your first social media post to get started'}
              </p>
              {!search && (
                <Button
                  onClick={() => navigate('/admin/outreach/social/new')}
                  className="mt-4"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Post
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {posts.map((post) => (
                <SocialPostCard
                  key={post.id}
                  post={post}
                  onDuplicate={handleDuplicate}
                  onDelete={setDeletePost}
                />
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t dark:border-gray-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Page {page} of {totalPages} ({total} posts)
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(1)}
                      disabled={!hasPrev}
                    >
                      First
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={!hasPrev}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-gray-500 dark:text-gray-400 px-2">
                      {page}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={!hasNext}
                    >
                      Next
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(totalPages)}
                      disabled={!hasNext}
                    >
                      Last
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Tabs>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deletePost} onOpenChange={() => setDeletePost(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Post</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletePost?.title}"? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SocialMediaManager;
